import React from 'react';

import Translate, { useI18n } from '@hydra/common/components/i18n';
import Icon from '@hydra/common/components/Icon';

import CriteriumTable from '@hydra/common/criterium/CriteriumTable';
import Criterium from '@hydra/common/criterium/Criterium';
import ReviewSkeleton from '@hydra/common/components/ReviewSkeleton';
import { CriteriumInterface } from '@hydra/types/review';

export function DeltaIconAndData ({
  data = 0,
  delta = null,
  isPercentage = false
}: {
  data: number
  delta: number | null
  isPercentage?: boolean
}) {
  const { numeral } = useI18n();
  const parsedData = isPercentage ? numeral(data).format('0%') : numeral(data).format();

  if (delta && delta > 0) {
    return (
      <span>
        {parsedData}
        <Icon type='triangle-up' color='success' size='xs' />
      </span>
    );
  }
  if (delta && delta < 0) {
    return (
      <span>
        {parsedData}
        <Icon type='triangle-down' color='error' size='xs' />
      </span>
    );
  }

  return (
    <span>
      {parsedData}
      <Icon type='blank' color='grey' size='xs' />
    </span>
  );
}

interface GwtQueriesCriterium extends CriteriumInterface {
  data: {
    queries: {
      query: string
      searchAnalytics: {
        clicks: number
        clicksDelta: number
        impressions: number
        impressionsDelta: number | null
        ctr: number
        ctrDelta: number
        position: number
        positionDelta: number
      }
    }[]
  }
}

export function GwtQueries ({ url, criterium }: { url: string, criterium: GwtQueriesCriterium }) {
  if (!criterium) {
    return (
      <Criterium criterium={{ type: 'gwt_queries', score: null, importance: 0, solvability: 0, status: 'neutral', url }} clickable={false}>
        <ReviewSkeleton height={451} />
      </Criterium>
    );
  }

  const { data } = criterium;

  const head = [
    <Translate content='Queries' key='Queries' />,
    <Translate content='Impressions' key='Impressions' />,
    <Translate content='Clicks' key='Clicks' />,
    <Translate content='CTR' key='CTR' />,
    <Translate content='Position' key='Position' />
  ];

  const body = data.queries.map(queryObject => {
    const { query } = queryObject;
    const {
      clicks,
      clicksDelta,
      impressions,
      impressionsDelta,
      ctr,
      ctrDelta,
      position,
      positionDelta
    } = queryObject.searchAnalytics;

    return [
      <span key='query'>{query}</span>,
      <DeltaIconAndData data={impressions} delta={impressionsDelta} key='impressions' />,
      <DeltaIconAndData data={clicks} delta={clicksDelta} key='clicks' />,
      <DeltaIconAndData isPercentage data={ctr} delta={ctrDelta} key='ctr' />,
      <DeltaIconAndData data={position} delta={positionDelta} key='position' />
    ];
  });

  const classNames = {
    head: ['', 'alignRight', 'alignRight', 'alignRight', 'alignRight'],
    body: ['', 'alignRight', 'alignRight', 'alignRight', 'alignRight']
  };

  const content = (
    <CriteriumTable
      showAmount={10}
      head={head}
      rows={body}
      fixedColumns={[27, 23, 19, 15, 16]}
      classNames={classNames}
      thinHead
      striped
      nowrap
    />
  );

  return (
    <Criterium
      criterium={criterium}
      variables={{ domain: url }}
    >
      {content}
    </Criterium>
  );
}

export default React.memo(GwtQueries);
